// src/components/AdminModals.jsx file name updated
import { useState } from 'react';
import { CheckCircle, DollarSign, RefreshCw } from 'lucide-react';
import { AdminResyncPanel } from './AdminResyncPanel';
import { LookupMemberPanel } from './LookupMemberPanel';
import { MemberViewModal } from './MemberViewModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function AdminModals({ 
  showReports, 
  setShowReports,
  showAdminPanel,
  setShowAdminPanel,
  showCreditBalances,
  setShowCreditBalances,
  showLookupMember,
  setShowLookupMember,
  showVisitStats,        // ← add
  setShowVisitStats,      // ← add
  showMemberView,       // ← add
  setShowMemberView     // ← add
}) {
  // Reports state
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Credit balances state
  const [creditBalanceData, setCreditBalanceData] = useState(null);
  const [creditBalanceLoading, setCreditBalanceLoading] = useState(false);
  //user tracker
  const [visitStatsData, setVisitStatsData] = useState(null);
  const [visitStatsLoading, setVisitStatsLoading] = useState(false);

  const handleGenerateReport = async () => {
    setReportLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/members-owing`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate report');
      }
      
      const data = await response.json();
      setReportData(data);
    } catch (err) {
      console.error('Report fetch failed:', err.message);
      alert('Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadReport = () => {
    let csv = `CBA Members Not Current Report - Year to Date\n`;
    csv += `Generated: ${reportData.generatedDate}\n`;
    csv += `Report Period: ${reportData.reportPeriod}\n`;
    csv += `\n`;
    csv += `Totals:\n`;
    csv += `Total Households: ${reportData.header.totalHouseholds}\n`;
    csv += `Total Events: ${reportData.header.totalEvents}\n`;
    csv += `Households with Owing Balance: ${reportData.header.householdsWithOwing}\n`;
    csv += `\n`;
    csv += `Name,Total Payments,Amount Owing\n`;
    reportData.csvData.forEach(row => {
      const totalPayments = typeof row.totalPayments === 'number' ? row.totalPayments : 0;
      const amountOwing = typeof row.amountOwing === 'number' ? row.amountOwing : 0;
      csv += `"${row.memberNames}","$${totalPayments.toFixed(2)}","$${amountOwing.toFixed(2)}"\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', reportData.header.fileName);
    link.click();
  };

  const handleGenerateCreditBalances = async () => {
    setCreditBalanceLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/credit-balances`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch credit balance report');
      }
      
      const data = await response.json();
      setCreditBalanceData(data);
    } catch (err) {
      console.error('Credit balance report failed:', err.message);
      alert('Failed to generate report');
    } finally {
      setCreditBalanceLoading(false);
    }
  };

  const handleDownloadCreditBalances = () => {
    let csv = `Credit Balance Report\n`;
    csv += `${creditBalanceData.header?.generatedDate}\n\n`;
    csv += `Member Names,Starting Credit,Calculated Credit,Total Credit\n`;
    creditBalanceData.csvData.forEach(row => {
      csv += `"${row.memberNames}","$${row.startingCredit.toFixed(2)}","$${row.calculatedCredit.toFixed(2)}","$${row.displayCredit.toFixed(2)}"\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', creditBalanceData.header?.fileName || 'credit_balances.csv');
    link.click();
  };

  return (
    <>
      {/* Reports Modal */}
      {showReports && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Members Owing Report</h2>
              <button
                onClick={() => {
                  setShowReports(false);
                  setReportData(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            {!reportData ? (
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-900">
                    <span className="font-semibold">Year-to-Date Report:</span> This report shows all events and payments from January 1 through today.
                  </p>
                </div>
                <button 
                  onClick={handleGenerateReport}
                  disabled={reportLoading}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                >
                  <DollarSign className="w-5 h-5" />
                  {reportLoading ? 'Generating Report...' : 'Generate Year-to-Date Report'}
                </button>
              </div>
            ) : (
              <div>
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="space-y-2 text-sm text-green-800">
                        <p><span className="font-semibold">Total Households:</span> {reportData.header.totalHouseholds}</p>
                        <p><span className="font-semibold">Total Events:</span> {reportData.header.totalEvents}</p>
                        <p><span className="font-semibold">Households with Owing Balance:</span> {reportData.header.householdsWithOwing}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {reportData.csvData.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No members with owing balances</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <button
                      onClick={handleDownloadReport}
                      className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
                    >
                      <DollarSign className="w-5 h-5" />
                      Download CSV Report
                    </button>

                    <div className="bg-gray-50 rounded-lg p-4 max-h-[300px] overflow-y-auto">
                      <h3 className="font-semibold text-gray-800 mb-3 text-sm">
                        Members with Owing Balances ({reportData.csvData.length})
                      </h3>
                      <div className="space-y-2">
                        {reportData.csvData.map((row, index) => (
                          <div key={index} className="flex justify-between text-sm bg-white p-2 rounded border border-gray-200">
                            <div>
                              <div className="text-gray-800 font-medium">{row.memberNames}</div>
                              <div className="text-gray-600 text-xs">
                                Paid: ${(typeof row.totalPayments === 'number' ? row.totalPayments : 0).toFixed(2)}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-red-600">
                                ${(typeof row.amountOwing === 'number' ? row.amountOwing : 0).toFixed(2)}
                              </div>
                              <div className="text-gray-600 text-xs">Owing</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                <button
                  onClick={() => setReportData(null)}
                  className="mt-6 w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 font-medium"
                >
                  Generate Another Report
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin Resync Panel Modal */}
      {showAdminPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowAdminPanel(false)}
              className="text-gray-500 hover:text-gray-700 text-2xl float-right mb-4"
            >
              ✕
            </button>
            <AdminResyncPanel />
          </div>
        </div>
      )}

      {/* Credit Balances Modal */}
      {showCreditBalances && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setShowCreditBalances(false);
                setCreditBalanceData(null);
              }}
              className="text-gray-500 hover:text-gray-700 text-2xl float-right mb-4"
            >
              ✕
            </button>
            
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Credit Balance Report</h2>
                <p className="text-gray-600 text-sm">
                  View all members with credit balances.
                </p>
              </div>

              {!creditBalanceData ? (
                <button
                  onClick={handleGenerateCreditBalances}
                  disabled={creditBalanceLoading}
                  className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  {creditBalanceLoading ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-5 h-5" />
                      Generate Report
                    </>
                  )}
                </button>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-sm text-green-800">{creditBalanceData.header?.generatedDate}</p>
                        <p className="font-semibold text-green-900">
                          {creditBalanceData.header?.totalWithCredit} households with credit
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleDownloadCreditBalances}
                    className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
                  >
                    <DollarSign className="w-5 h-5" />
                    Download CSV Report
                  </button>

                  {creditBalanceData.csvData.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No members with credit balances</p>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 max-h-[300px] overflow-y-auto">
                      <h3 className="font-semibold text-gray-800 mb-3 text-sm">
                        Members with Credit ({creditBalanceData.csvData.length})
                      </h3>
                      <div className="space-y-2">
                        {creditBalanceData.csvData.map((row, index) => (
                          <div key={index} className="flex justify-between text-sm bg-white p-2 rounded border border-gray-200">
                            <div>
                              <div className="text-gray-800 font-medium">{row.memberNames}</div>
                              <div className="text-gray-600 text-xs">
                                Starting: ${row.startingCredit.toFixed(2)} | Calculated: ${row.calculatedCredit.toFixed(2)}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-green-600">${row.displayCredit.toFixed(2)}</div>
                              <div className="text-gray-600 text-xs">Total</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => setCreditBalanceData(null)}
                    className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 font-medium"
                  >
                    Generate Another Report
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lookup Member Modal */}
      {showLookupMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowLookupMember(false)}
              className="text-gray-500 hover:text-gray-700 text-2xl float-right mb-4"
            >
              ✕
            </button>
            <LookupMemberPanel />
          </div>
        </div>
      )}
      {/* Visit Stats Modal */}
      {showVisitStats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => { setShowVisitStats(false); setVisitStatsData(null); }}
              className="text-gray-500 hover:text-gray-700 text-2xl float-right mb-4"
            >
              ✕
            </button>
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Dashboard Visit Stats</h2>
                <p className="text-gray-600 text-sm">Member visits for {new Date().getFullYear()}.</p>
              </div>

              {!visitStatsData ? (
                <button
                  onClick={async () => {
                    setVisitStatsLoading(true);
                    try {
                      const response = await fetch(`${API_URL}/api/reports/dashboard-visits`, {
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' }
                      });
                      if (!response.ok) throw new Error('Failed to fetch visit stats');
                      const data = await response.json();
                      setVisitStatsData(data);
                    } catch (err) {
                      console.error('Visit stats failed:', err.message);
                      alert('Failed to load visit stats');
                    } finally {
                      setVisitStatsLoading(false);
                    }
                  }}
                  disabled={visitStatsLoading}
                  className="w-full bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                >
                  {visitStatsLoading ? (
                    <><RefreshCw className="w-5 h-5 animate-spin" /> Loading...</>
                  ) : 'Load Visit Stats'}
                </button>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-indigo-700">{visitStatsData.totalVisits}</div>
                      <div className="text-sm text-indigo-600 mt-1">Total Visits</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-purple-700">{visitStatsData.uniqueVisitors}</div>
                      <div className="text-sm text-purple-600 mt-1">Unique Visitors</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 max-h-[300px] overflow-y-auto">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Visits per Member</h3>
                    <div className="space-y-2">
                      {visitStatsData.perMember.map((row, index) => (
                        <div key={index} className="flex justify-between items-center text-sm bg-white p-2 rounded border border-gray-200">
                          <span className="text-gray-800 font-medium">{row.name}</span>
                          <span className="bg-indigo-100 text-indigo-700 font-semibold px-2 py-0.5 rounded-full text-xs">
                            {row.visitCount} visit{row.visitCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => setVisitStatsData(null)}
                    className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 font-medium"
                  >
                    Refresh
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Member View Modal */}
      {showMemberView && (
        <MemberViewModal onClose={() => setShowMemberView(false)} />
      )}
    </>
  );
}